import React from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Switch, Dropdown } from 'antd'
import { getZeroDarkWebLocale, ZERO_DARK_WEB_URLS } from 'dUtils'
import store from 'store'
import { store as reduxStore } from 'index'
import { getTempEnvParam } from 'env'
import SettingsService from '../api/Settings.ts'

import arrowIcon from '@/assets/images/cheveron-down-24.svg'
import zeroDarkWebIcon from '@/assets/images/service/service-zero-darkweb.svg'

import styles from '../style.module.scss'

export const handleSaveZeroDarkWeb = async (settings, messageApi, t) => {
  const zdwSettings = reduxStore.getState().settings.zeroDarkWebSettings
  const previousActive = zdwSettings?.zdw_active === true || zdwSettings?.zdw_active === 'true'
  const isActiveNow = !previousActive && settings.switch

  const previousDuration =
    zdwSettings?.zdw_duration != null ? String(zdwSettings.zdw_duration) : null
  const currentInterval = settings.interval || '7'
  const isDurationChanged =
    previousDuration !== currentInterval && previousActive && settings.switch

  let expiredAt = null
  let checkedAt = null
  if (settings.switch) {
    if (isActiveNow) {
      checkedAt = new Date().toISOString()
      if (currentInterval === 'test') {
        expiredAt = new Date(Date.now() + 1 * 60 * 1000).toISOString()
      } else {
        expiredAt = new Date(
          Date.now() + parseInt(currentInterval, 10) * 24 * 60 * 60 * 1000,
        ).toISOString()
      }
    } else if (isDurationChanged) {
      const baseDate = zdwSettings?.zdw_checked_at
        ? new Date(zdwSettings.zdw_checked_at)
        : new Date()
      const baseTime = baseDate.getTime()

      if (currentInterval === 'test') {
        expiredAt = new Date(baseTime + 1 * 60 * 1000).toISOString()
      } else {
        expiredAt = new Date(
          baseTime + parseInt(currentInterval, 10) * 24 * 60 * 60 * 1000,
        ).toISOString()
      }
    }
  }

  const settingsToSave = {
    zdw_active: String(settings.switch),
    ...(settings.switch ? { zdw_duration: currentInterval } : { zdw_duration: null }),
    ...(expiredAt ? { zdw_expired_at: expiredAt } : { zdw_expired_at: null }),
  }

  // API 호출을 직접 수행하여 응답을 받습니다
  const zdwSettingStore = store.get('app.settings.zeroDarkWebSettings')
  const object = {
    ...zdwSettingStore,
    ...settingsToSave,
  }

  const result = await SettingsService.put({
    zeroDarkWebSettings: Object.entries(object).map(([key, value]) => ({
      label: key,
      content: String(value),
    })),
  })

  // API 응답 확인
  if (result?.code !== 200) {
    console.error('❌ Error saving ZeroDarkWeb settings:', result?.message)
    messageApi.error(t('appConfig.settings.errorSaveZeroDarkWeb') || 'Failed to save settings')
    return
  }
  messageApi.success(t('appConfig.settings.successSaveZeroDarkWeb'))

  reduxStore.dispatch({
    type: 'settings/CHANGE_SETTING',
    payload: {
      setting: 'zeroDarkWebSettings',
      value: object,
    },
  })

  const tablePage = getTempEnvParam('curTablePage')
  if (!settings.switch) {
    reduxStore.dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'showZeroDarkWeb',
        value: false,
      },
    })

    reduxStore.dispatch({
      type: 'settings/SET_IS_LEAKED',
      payload: {
        isLeaked: false,
      },
    })

    if (tablePage) {
      tablePage.reqData(null)
    }
  } else {
    console.log('🔄 ZeroDarkWeb activated from Settings, dispatching event...')

    if (isActiveNow) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(
            new CustomEvent('darkWebFetchData', { detail: { timestamp: checkedAt } }),
          )
        })
      })

      reduxStore.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'showZeroDarkWeb',
          value: true,
        },
      })
    }
  }
}

const intervalLabelMap = (t) => {
  const map = {
    1: t('dark-web.interval1Day'),
    7: t('dark-web.interval1Week'),
    30: t('dark-web.interval1Month'),
  }
  if (process.env.REACT_APP_TARGET !== 'production') {
    map.test = 'test'
  }
  return map
}

const ZeroDarkWebSettingsComponent = ({ settings, onChange }) => {
  const { t } = useTranslation()

  // 저장 버튼 제거 — 스위치/검사 주기 변경 즉시 상위(onChange)에서 로컬 상태 갱신 + 서버 저장.
  const handleDropdownClick = (key) => {
    onChange({ ...settings, interval: key })
  }

  const handleSwitchChange = (checked) => {
    onChange({ ...settings, switch: checked })
  }

  const dropdownItems = [
    ...(process.env.REACT_APP_TARGET !== 'production'
      ? [
          {
            key: 'test',
            label: 'test(1 min)',
            onClick: ({ key }) => handleDropdownClick(key),
          },
        ]
      : []),
    {
      key: '1',
      label: t('dark-web.interval1Day'),
      onClick: ({ key }) => handleDropdownClick(key),
    },
    {
      key: '7',
      label: t('dark-web.interval1Week'),
      onClick: ({ key }) => handleDropdownClick(key),
    },
    {
      key: '30',
      label: t('dark-web.interval1Month'),
      onClick: ({ key }) => handleDropdownClick(key),
    },
  ]

  return (
    <div className={classNames(styles.zeroDarkWebContent)}>
      <div className={styles.zeroDarkWebOption}>
        <span className={styles.zeroDarkWebLabel}>{t('dark-web.useZeroDarkWebReport')}</span>
        <Switch
          className={classNames(styles.passkeySwitch)}
          checked={settings.switch}
          onChange={handleSwitchChange}
        />
      </div>
      {settings.switch && (
        <div className={styles.checkIntervalOption}>
          <span className={styles.zeroDarkWebLabel}>{t('dark-web.checkInterval')}</span>
          <div className={styles.spacer} />
          <Dropdown
            menu={{
              items: dropdownItems,
            }}
            trigger={['click']}
            defaultOpen={settings.interval != null ? settings.interval : 7}
          >
            <div className={styles.intervalDropdown}>
              <span className={styles.intervalValue}>
                {intervalLabelMap(t)[settings.interval] || settings.interval}
              </span>
              <img src={arrowIcon} alt="chevron-down" className={styles.chevronIcon} />
            </div>
          </Dropdown>
        </div>
      )}

      <div className={styles.divider} />
      <div className={styles.poweredBy}>
        <img src={zeroDarkWebIcon} alt="ZeroDarkWeb" className={styles.zeroDarkWebLogo} />
        <span className={styles.poweredByText}>
          {t('dark-web.poweredBy')}

          <a
            href={ZERO_DARK_WEB_URLS[getZeroDarkWebLocale()] || ZERO_DARK_WEB_URLS.default}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.zeroDarkWebLink}
          >
            {' '}
            ZeroDarkWeb
          </a>
        </span>
      </div>
    </div>
  )
}

export default ZeroDarkWebSettingsComponent
